import { SponsorsByEntity } from '@/models/CompanyEntity';
import { CounterpartyExposureData } from '@/models/Counterparty/Exposure';
import { Files } from '@/types/files';

import { ActionType, getType } from 'typesafe-actions';

import { TotalsInitialState } from '../types';
import {
  EntityData,
  EntityDebt,
  EntityFlows,
  EntityTransactionData,
  HedgedNotionalData,
  HistoricalEntityTransactions,
  ScenarioAnalysisData,
} from '../types';

import * as entityPortfolioActions from './actions';

type EntiityPortfollioActionType = ActionType<typeof entityPortfolioActions>;

export interface EntityPortfolioState {
  entityTransactions: {
    data: EntityTransactionData;
    loading: boolean;
  };
  scenarioAnalysisLoading: boolean;

  historicalEntityTransactions: {
    data: HistoricalEntityTransactions[];
    loading: boolean;
  };
  upcomingCashFlowsLoading: boolean;
  entityData: {
    data: EntityData;
    loading: boolean;
  };
  entityFiles: {
    data: Files;
    loading: boolean;
  };
  entityDebt: {
    data: EntityDebt;
    loading: boolean;
  };

  counterPartyExposure: {
    data: CounterpartyExposureData;
    loading: boolean;
  };

  debtAndHedge: {
    data: HedgedNotionalData[];
    loading: boolean;
  };

  debtValuationLoading: boolean;
  debtStatementLoading: boolean;
  debtUpcomingCashflowsLoading: boolean;
  debtAccrualsLoading: boolean;

  entityCashFlows: {
    data: EntityFlows[];
    loading: boolean;
  };

  ifrsReportLoading: boolean;

  ifrsTrades: {
    data: number[];
    loading: boolean;
  };

  ccsReportLoading: boolean;
  valuationDate: string;
  entitySponsor: {
    data: SponsorsByEntity | null;
    loading: boolean;
  };
  scenarioAnalysisData: {
    data: ScenarioAnalysisData | null;
    loading: boolean;
  };
}

const initialState: EntityPortfolioState = {
  entityTransactions: {
    data: {
      transactions: [],
      totals: TotalsInitialState,
    },
    loading: false,
  },
  scenarioAnalysisLoading: false,

  historicalEntityTransactions: {
    data: [],
    loading: false,
  },
  upcomingCashFlowsLoading: false,
  entityData: {
    data: {
      entity_id: '',
      entity_name: '...',
    },
    loading: false,
  },
  entityFiles: {
    data: {
      generic_files: [],
      scheduled_deliverables_files: [],
    },
    loading: false,
  },
  entityDebt: {
    data: {
      active: [],
      inactive: [],
      active_totals: TotalsInitialState,
    },
    loading: false,
  },

  counterPartyExposure: {
    data: {
      counterparty_data: [],
      value_date: '',
      value_total: 0,
    },
    loading: false,
  },

  debtAndHedge: {
    data: [],
    loading: false,
  },

  debtValuationLoading: false,
  debtStatementLoading: false,
  debtUpcomingCashflowsLoading: false,
  debtAccrualsLoading: false,

  entityCashFlows: {
    data: [],
    loading: false,
  },

  ifrsReportLoading: false,

  ifrsTrades: {
    data: [],
    loading: false,
  },

  ccsReportLoading: false,

  valuationDate: '',

  entitySponsor: {
    data: null,
    loading: false,
  },

  scenarioAnalysisData: {
    data: null,
    loading: false,
  },
};

export const entityPortfolioReducer = (
  state: EntityPortfolioState = initialState,
  action: EntiityPortfollioActionType,
): EntityPortfolioState => {
  switch (action.type) {
    // entity transactions
    case getType(entityPortfolioActions.getEntityTransactions.request):
      return {
        ...state,
        entityTransactions: {
          ...state.entityTransactions,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.getEntityTransactions.success):
      return {
        ...state,
        entityTransactions: {
          ...state.entityTransactions,
          data: action.payload.data,
          loading: false,
        },
      };
    // scenario analysis
    case getType(
      entityPortfolioActions.downloadScenarioAnnalysisReport.request,
    ):
      return {
        ...state,
        scenarioAnalysisLoading: true,
      };
    case getType(
      entityPortfolioActions.downloadScenarioAnnalysisReport.success,
    ):
      return {
        ...state,
        scenarioAnalysisLoading: false,
      };
    case getType(
      entityPortfolioActions.downloadScenarioAnnalysisReport.failure,
    ):
      return {
        ...state,
        scenarioAnalysisLoading: false,
      };

    //historical trades
    case getType(
      entityPortfolioActions.getHistoricalEntityTransactions.request,
    ):
      return {
        ...state,
        historicalEntityTransactions: {
          ...state.historicalEntityTransactions,
          loading: true,
        },
      };
    case getType(
      entityPortfolioActions.getHistoricalEntityTransactions.success,
    ):
      return {
        ...state,
        historicalEntityTransactions: {
          ...state.historicalEntityTransactions,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(
      entityPortfolioActions.getHistoricalEntityTransactions.failure,
    ):
      return {
        ...state,
        historicalEntityTransactions: {
          ...state.historicalEntityTransactions,
          loading: false,
        },
      };

    //upcoming cash flows
    case getType(
      entityPortfolioActions.downloadUpcomingCashdlowsReport.request,
    ):
      return {
        ...state,
        upcomingCashFlowsLoading: true,
      };
    case getType(
      entityPortfolioActions.downloadUpcomingCashdlowsReport.success,
    ):
      return {
        ...state,
        upcomingCashFlowsLoading: false,
      };
    case getType(
      entityPortfolioActions.downloadUpcomingCashdlowsReport.failure,
    ):
      return {
        ...state,
        upcomingCashFlowsLoading: false,
      };

    // entity data
    case getType(entityPortfolioActions.getEntityById.request):
      return {
        ...state,
        entityData: {
          ...state.entityData,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.getEntityById.success):
      return {
        ...state,
        entityData: {
          ...state.entityData,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(entityPortfolioActions.getEntityById.failure):
      return {
        ...state,
        entityData: {
          ...state.entityData,
          loading: false,
        },
      };

    //entity files
    case getType(entityPortfolioActions.getEntityFiles.request):
      return {
        ...state,
        entityFiles: {
          ...state.entityFiles,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.getEntityFiles.success):
      return {
        ...state,
        entityFiles: {
          ...state.entityFiles,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(entityPortfolioActions.getEntityFiles.failure):
      return {
        ...state,
        entityFiles: {
          ...state.entityFiles,
          loading: false,
        },
      };

    // entity debt
    case getType(entityPortfolioActions.getEntityDebt.request):
      return {
        ...state,
        entityDebt: {
          ...state.entityDebt,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.getEntityDebt.success):
      return {
        ...state,
        entityDebt: {
          ...state.entityDebt,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(entityPortfolioActions.getEntityDebt.failure):
      return {
        ...state,
        entityDebt: {
          ...state.entityDebt,
          loading: false,
        },
      };

    // counterparty exposure
    case getType(entityPortfolioActions.setCounterPartyExposure.request):
      return {
        ...state,
        counterPartyExposure: {
          ...state.counterPartyExposure,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.setCounterPartyExposure.success):
      return {
        ...state,
        counterPartyExposure: {
          ...state.counterPartyExposure,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(entityPortfolioActions.setCounterPartyExposure.failure):
      return {
        ...state,
        counterPartyExposure: {
          ...state.counterPartyExposure,
          loading: false,
        },
      };

    // debt and hedge
    case getType(entityPortfolioActions.getDebtAndHedge.request):
      return {
        ...state,
        debtAndHedge: {
          ...state.debtAndHedge,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.getDebtAndHedge.success):
      return {
        ...state,
        debtAndHedge: {
          ...state.debtAndHedge,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(entityPortfolioActions.getDebtAndHedge.failure):
      return {
        ...state,
        debtAndHedge: {
          ...state.debtAndHedge,
          loading: false,
        },
      };

    // debt valuation
    case getType(entityPortfolioActions.getDebtValuationReport.request):
      return {
        ...state,
        debtValuationLoading: true,
      };
    case getType(entityPortfolioActions.getDebtValuationReport.success):
      return {
        ...state,
        debtValuationLoading: false,
      };
    case getType(entityPortfolioActions.getDebtValuationReport.failure):
      return {
        ...state,
        debtValuationLoading: false,
      };

    // debt statement
    case getType(entityPortfolioActions.getDebtStatementReport.request):
      return {
        ...state,
        debtStatementLoading: true,
      };
    case getType(entityPortfolioActions.getDebtStatementReport.success):
      return {
        ...state,
        debtStatementLoading: false,
      };
    case getType(entityPortfolioActions.getDebtStatementReport.failure):
      return {
        ...state,
        debtStatementLoading: false,
      };

    // debt accruals
    case getType(entityPortfolioActions.getDebtAccrualsReport.request):
      return {
        ...state,
        debtAccrualsLoading: true,
      };
    case getType(entityPortfolioActions.getDebtAccrualsReport.success):
      return {
        ...state,
        debtAccrualsLoading: false,
      };
    case getType(entityPortfolioActions.getDebtAccrualsReport.failure):
      return {
        ...state,
        debtAccrualsLoading: false,
      };

    // debt cashflows
    case getType(entityPortfolioActions.getDebtUpcomingCashflowsReport.request):
      return {
        ...state,
        debtUpcomingCashflowsLoading: true,
      };
    case getType(entityPortfolioActions.getDebtUpcomingCashflowsReport.success):
      return {
        ...state,
        debtUpcomingCashflowsLoading: false,
      };
    case getType(entityPortfolioActions.getDebtUpcomingCashflowsReport.failure):
      return {
        ...state,
        debtUpcomingCashflowsLoading: false,
      };

    // cashflows
    case getType(entityPortfolioActions.getEntityCashflows.request):
      return {
        ...state,
        entityCashFlows: {
          ...state.entityCashFlows,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.getEntityCashflows.success):
      return {
        ...state,
        entityCashFlows: {
          ...state.entityCashFlows,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(entityPortfolioActions.getEntityCashflows.failure):
      return {
        ...state,
        entityCashFlows: {
          ...state.entityCashFlows,
          loading: false,
        },
      };

    // ifrs report
    case getType(entityPortfolioActions.getIFRSReport.request):
      return {
        ...state,
        ifrsReportLoading: true,
      };
    case getType(entityPortfolioActions.getIFRSReport.success):
      return {
        ...state,
        ifrsReportLoading: false,
      };
    case getType(entityPortfolioActions.getIFRSReport.failure):
      return {
        ...state,
        ifrsReportLoading: false,
      };

    //get ifrs trades
    case getType(entityPortfolioActions.getIFRSTrades.request):
      return {
        ...state,
        ifrsTrades: {
          ...state.ifrsTrades,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.getIFRSTrades.success):
      return {
        ...state,
        ifrsTrades: {
          ...state.ifrsTrades,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(entityPortfolioActions.getIFRSTrades.failure):
      return {
        ...state,
        ifrsTrades: {
          ...state.ifrsTrades,
          loading: false,
        },
      };

    //ccs report
    case getType(entityPortfolioActions.getCCSReport.request):
      return {
        ...state,
        ccsReportLoading: true,
      };
    case getType(entityPortfolioActions.getCCSReport.success):
      return {
        ...state,
        ccsReportLoading: false,
      };
    case getType(entityPortfolioActions.getCCSReport.failure):
      return {
        ...state,
        ccsReportLoading: false,
      };

    //store valuation date
    case getType(entityPortfolioActions.setDate):
      return {
        ...state,
        valuationDate: action.payload,
      };

    //entity sponsor
    case getType(entityPortfolioActions.getSponsorsByEntities.request):
      return {
        ...state,
        entitySponsor: {
          ...state.entitySponsor,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.getSponsorsByEntities.success):
      return {
        ...state,
        entitySponsor: {
          ...state.entitySponsor,
          data: action.payload.data[0] || [],
          loading: false,
        },
      };
    case getType(entityPortfolioActions.getSponsorsByEntities.failure):
      return {
        ...state,
        entitySponsor: {
          ...state.entitySponsor,
          loading: false,
        },
      };

    // scenario analysis data
    case getType(entityPortfolioActions.getScenarioAnalysisData.request):
      return {
        ...state,
        scenarioAnalysisData: {
          ...state.scenarioAnalysisData,
          loading: true,
        },
      };
    case getType(entityPortfolioActions.getScenarioAnalysisData.success):
      return {
        ...state,
        scenarioAnalysisData: {
          ...state.scenarioAnalysisData,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(entityPortfolioActions.getScenarioAnalysisData.failure):
      return {
        ...state,
        scenarioAnalysisData: {
          ...state.scenarioAnalysisData,
          loading: false,
        },
      };
    default:
      return state;
  }
};
